import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, MapPin, CalendarBlank, CheckCircle, Stethoscope } from "@phosphor-icons/react";
import Seo, { SITE } from "@/components/site/Seo";
import { PetPhotoStrip } from "@/components/site/PetPhotoStrip";
import { breadcrumbJsonLd, faqJsonLd } from "@/lib/schema";
import { properties } from "@/data/properties";

const fade = (delay = 0) => ({
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.7, delay, ease: [0.16, 1, 0.3, 1] },
});

export default function LandingPage({ config }) {
  const homes = config.propertySlugs
    .map((slug) => properties.find((p) => p.slug === slug))
    .filter(Boolean);

  const itemList = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: config.h1,
    itemListElement: homes.map((p, i) => ({
      "@type": "ListItem",
      position: i + 1,
      item: { "@type": "LodgingBusiness", name: p.name, url: `${SITE}/properties/${p.slug}` },
    })),
  };

  return (
    <main data-testid="landing-page" className="pt-32 md:pt-40 pb-24 md:pb-32">
      <Seo
        title={config.metaTitle}
        description={config.metaDescription}
        keywords={config.keywords}
        canonical={`${SITE}/${config.slug}`}
        image={config.ogImage ? `${SITE}${config.ogImage}` : undefined}
        jsonLd={[
          itemList,
          faqJsonLd(config.faqs),
          breadcrumbJsonLd([
            { name: "Home", path: "/" },
            { name: config.h1, path: `/${config.slug}` },
          ]),
        ]}
      />

      {/* Hero + direct-answer lead */}
      <div className="max-w-[1000px] mx-auto px-6 md:px-10">
        <motion.div {...fade()}>
          <p className="overline text-sunset mb-5 flex items-center gap-2">
            <MapPin size={16} weight="fill" /> {config.category}
          </p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tight">
            {config.h1}
          </h1>
          <p data-testid="landing-answer" className="text-lg md:text-xl text-emerald-soft font-light leading-relaxed max-w-3xl mt-7">
            {config.answer}
          </p>
          <div className="mt-8 flex flex-wrap gap-4">
            <Link
              to="/properties"
              data-testid="landing-cta-book"
              className="group inline-flex items-center gap-2 rounded-full bg-sunset px-8 py-4 text-sand font-medium transition-colors duration-300 hover:bg-gold hover:text-emerald"
            >
              Check availability
              <span className="transition-transform duration-300 group-hover:translate-x-1">→</span>
            </Link>
            <Link
              to="/contact"
              data-testid="landing-cta-contact"
              className="inline-flex items-center gap-2 rounded-full border border-emerald/25 px-8 py-4 text-emerald font-medium transition-colors duration-300 hover:bg-emerald/5"
            >
              Ask a question
            </Link>
          </div>
        </motion.div>
      </div>

      {/* Guest pet photo strip (pet-friendly pages) */}
      {config.petPhotoStrip && <PetPhotoStrip />}

      {/* Vet care guide (pet-friendly pages) */}
      {config.vetCare && (
        <section data-testid="landing-vet-care" className="max-w-[1000px] mx-auto px-6 md:px-10 mt-16">
          <motion.div {...fade()} className="border border-emerald/10 rounded-[28px] bg-sand p-8 md:p-12">
            <p className="overline text-sunset mb-4 flex items-center gap-2">
              <Stethoscope size={16} weight="duotone" /> Good vets, close by
            </p>
            <h2 className="font-display text-2xl md:text-3xl tracking-tight mb-4" data-testid="vet-care-heading">
              {config.vetCare.heading}
            </h2>
            <p className="text-emerald-soft font-light leading-relaxed">{config.vetCare.intro}</p>
            <ul className="space-y-5 mt-8">
              {config.vetCare.vets.map((v) => (
                <li key={v.name} className="text-emerald-soft font-light leading-relaxed" data-testid={`vet-item-${v.name}`}>
                  <a
                    href={v.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-medium text-sunset hover:text-emerald transition-colors duration-300 underline decoration-sunset/30 underline-offset-4"
                  >
                    {v.name}
                  </a>
                  <span className="text-emerald/50 text-sm"> · {v.distance}</span> — {v.detail}.
                </li>
              ))}
            </ul>
            <p className="text-sm text-emerald-soft/80 font-light mt-8">{config.vetCare.closing}</p>
          </motion.div>
        </section>
      )}

      {/* Choose your rental (optional per-page field — linked editorial cards) */}
      {config.chooseRentals && (
        <div className="max-w-[1000px] mx-auto px-6 md:px-10 mt-16">
          <motion.h2 {...fade()} data-testid="landing-choose-heading" className="font-display text-2xl md:text-3xl tracking-tight mb-8">
            {config.chooseRentals.h}
          </motion.h2>
          <div className="grid sm:grid-cols-2 gap-6">
            {config.chooseRentals.items.map((r, i) => (
              <motion.div key={r.slug} {...fade((i % 2) * 0.06)}>
                <Link
                  to={`/properties/${r.slug}`}
                  data-testid={`landing-choose-${r.slug}`}
                  className="group block h-full rounded-2xl border border-emerald/12 bg-sand-2 p-6 transition-colors duration-300 hover:border-sunset/40"
                >
                  <h3 className="font-display text-xl tracking-tight group-hover:text-sunset transition-colors duration-300">{r.name}</h3>
                  <p className="overline text-sunset mt-2 mb-3">{r.stats}</p>
                  <p className="text-emerald-soft font-light leading-relaxed">{r.blurb}</p>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      )}

      {/* Seasonal callout (e.g. Thanksgiving on the cruise pages) */}
      {config.seasonalNote && (
        <div className="max-w-[1000px] mx-auto px-6 md:px-10 mt-12">
          <motion.div {...fade()} data-testid="landing-seasonal" className="rounded-3xl bg-emerald text-sand p-8 md:p-10">
            <p className="overline text-gold mb-2">{config.seasonalNote.h}</p>
            <p className="font-light leading-relaxed text-sand/85 max-w-3xl">{config.seasonalNote.body}</p>
            <Link
              to={config.seasonalNote.linkTo}
              data-testid="landing-seasonal-link"
              className="group inline-flex items-center gap-2 mt-5 text-gold font-medium hover:text-sand transition-colors duration-300"
            >
              {config.seasonalNote.linkLabel}
              <span className="transition-transform duration-300 group-hover:translate-x-1">→</span>
            </Link>
          </motion.div>
        </div>
      )}

      {/* Matching homes */}
      <div className="max-w-[1100px] mx-auto px-6 md:px-10 mt-20 md:mt-28">
        <motion.h2 {...fade()} className="font-display text-2xl md:text-3xl tracking-tight mb-8">
          {homes.length > 1 ? "Homes that fit" : "The home"}
        </motion.h2>
        <div className="grid sm:grid-cols-2 gap-6">
          {homes.map((p, i) => (
            <motion.div key={p.slug} {...fade((i % 2) * 0.06)}>
              <Link
                to={`/properties/${p.slug}`}
                data-testid={`landing-home-${p.slug}`}
                className="group block rounded-2xl overflow-hidden border border-emerald/12 bg-sand-2 transition-colors duration-300 hover:border-sunset/40"
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src={p.image}
                    alt={p.name}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <div className="p-6 flex items-start justify-between gap-4">
                  <div>
                    <p className="font-display text-xl md:text-2xl leading-snug text-emerald">{p.shortName}</p>
                    <p className="text-emerald-soft text-sm mt-1.5">
                      {p.location} · sleeps {p.guests} · {p.bedrooms} bd · {p.bathrooms} ba
                    </p>
                  </div>
                  <ArrowUpRight size={22} className="shrink-0 text-sunset transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Local context */}
      <div className="max-w-[1000px] mx-auto px-6 md:px-10 mt-20 md:mt-28">
        <div className="grid md:grid-cols-2 gap-x-16 gap-y-12">
          {config.context.map((c, i) => (
            <motion.section key={i} {...fade((i % 2) * 0.06)} data-testid={`landing-context-${i}`}>
              <h2 className="font-display text-2xl md:text-3xl leading-snug tracking-tight border-t border-emerald/15 pt-6">
                {c.h}
              </h2>
              <p className="text-emerald-soft font-light leading-relaxed mt-4">{c.body}</p>
              {c.list && (
                <ul className="mt-5 flex flex-col gap-3">
                  {c.list.map((item) => (
                    <li key={item} className="flex gap-3 text-emerald-soft font-light leading-relaxed">
                      <CheckCircle size={17} weight="fill" className="text-sunset shrink-0 mt-1" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              )}
            </motion.section>
          ))}
        </div>
      </div>

      {/* Cruise lines (cruise pages only) */}
      {config.linesSection && (
        <div className="max-w-[1000px] mx-auto px-6 md:px-10 mt-20 md:mt-28">
          <motion.section {...fade()} data-testid="landing-lines">
            <h2 className="font-display text-3xl md:text-4xl tracking-tight mb-4">{config.linesSection.h}</h2>
            <p className="text-emerald-soft font-light max-w-2xl mb-8">{config.linesSection.intro}</p>
            <ul className="grid sm:grid-cols-2 gap-x-12 gap-y-6">
              {config.linesSection.lines.map((l) => (
                <li key={l.name} className="border-t border-emerald/12 pt-4" data-testid={`landing-line-${l.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                  <p className="text-emerald font-medium">
                    {l.name} <span className="text-sunset text-sm font-normal">· {l.terminal}</span>
                  </p>
                  <p className="text-emerald-soft text-sm font-light mt-1">{l.note}</p>
                </li>
              ))}
            </ul>
            <p className="text-emerald-soft/70 text-sm font-light mt-7">{config.linesSection.outro}</p>
          </motion.section>
        </div>
      )}

      {/* When to book (cruise pages only) */}
      {config.whenToBook && (
        <div className="max-w-[1000px] mx-auto px-6 md:px-10 mt-16 md:mt-20">
          <motion.div
            {...fade()}
            data-testid="landing-when-to-book"
            className="rounded-3xl border border-sunset/30 bg-sunset/5 p-8 md:p-10 flex gap-5 items-start"
          >
            <span className="w-12 h-12 rounded-full bg-sunset/15 grid place-items-center shrink-0">
              <CalendarBlank size={22} weight="duotone" className="text-sunset" />
            </span>
            <div>
              <p className="overline text-sunset mb-2">{config.whenToBook.h}</p>
              <p className="text-emerald-soft font-light leading-relaxed">{config.whenToBook.body}</p>
            </div>
          </motion.div>
        </div>
      )}

      {/* Guest reviews (cruise pages only) */}
      {config.guestReviews && (
        <div className="max-w-[1000px] mx-auto px-6 md:px-10 mt-16 md:mt-20">
          <motion.h2 {...fade()} className="font-display text-3xl md:text-4xl tracking-tight mb-8">
            What recent guests say
          </motion.h2>
          <div className="grid md:grid-cols-2 gap-x-16 gap-y-9">
            {config.guestReviews.map((r, i) => (
              <motion.blockquote
                key={i}
                {...fade((i % 2) * 0.06)}
                className="border-t border-emerald/15 pt-6"
                data-testid={`landing-review-${i}`}
              >
                <p className="text-emerald-soft font-light leading-relaxed">&ldquo;{r.text}&rdquo;</p>
                <footer className="text-sm text-emerald font-medium mt-4">
                  {r.name} <span className="text-emerald-soft/70 font-normal">· {r.date}</span>
                </footer>
              </motion.blockquote>
            ))}
          </div>
        </div>
      )}

      {/* FAQ */}
      <div className="max-w-[1000px] mx-auto px-6 md:px-10 mt-20 md:mt-28">
        <motion.h2 {...fade()} className="font-display text-3xl md:text-4xl tracking-tight mb-8">
          Frequently asked
        </motion.h2>
        <div className="grid md:grid-cols-2 gap-x-16 gap-y-9">
          {config.faqs.map((f, i) => (
            <motion.div key={i} {...fade((i % 2) * 0.06)} className="border-t border-emerald/15 pt-6" data-testid={`landing-faq-${i}`}>
              <h3 className="font-display text-lg md:text-xl leading-snug text-emerald mb-3">{f.q}</h3>
              <p className="text-emerald-soft font-light leading-relaxed">{f.a}</p>
            </motion.div>
          ))}
        </div>
      </div>

      {/* CTA */}
      <div className="max-w-[1100px] mx-auto px-6 md:px-10 mt-24 md:mt-32">
        <motion.div {...fade()} className="rounded-3xl bg-emerald text-sand p-10 md:p-14 text-center" data-testid="landing-cta">
          <h2 className="font-display text-3xl md:text-4xl leading-tight tracking-tight max-w-2xl mx-auto">
            Ready when you are.
          </h2>
          <p className="text-sand/70 mt-4 max-w-xl mx-auto font-light">
            Book direct for the best rate, real-time availability and a host who actually replies.
          </p>
          {config.ctaNote && (
            <p className="text-gold text-sm mt-5 font-medium" data-testid="landing-cta-note">
              {config.ctaNote}
            </p>
          )}
          <Link
            to="/properties"
            data-testid="landing-cta-book-2"
            className="group inline-flex items-center gap-2 mt-8 rounded-full bg-sunset px-8 py-4 text-sand font-medium transition-colors duration-300 hover:bg-gold hover:text-emerald"
          >
            See all stays
            <span className="transition-transform duration-300 group-hover:translate-x-1">→</span>
          </Link>
        </motion.div>
      </div>
    </main>
  );
}
